import React, { useEffect, useState } from "react";
import {
  Typography,
  Container,
  Paper,
  makeStyles,
  Box,
} from "@material-ui/core";
import { useForm } from "react-hook-form";
import CompanyHeader from "./CompanyHeader";
import SubmitButton from "../component/SubmitButton";
import "../styles/styles.css";
import { useLocation, useNavigate } from "react-router-dom";
import HttpClient from "../api/HttpClient";
import { useLoading } from "../constant/LoadingContext";
import jseu from "js-encoding-utils";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import StaffFormFields from "../component/StaffFormFields";
import { transformFormErrors } from "../utils";


interface FormInputs {
  english_name: string;
  chinese_name: string;
  english_name_alias: string;
  hkid: string;
  hkid_last_digit: string;
  date_of_birth: string;
  nationality: string;
  gender: "M" | "F" | undefined;
  marriage_status: "single" | "married" | undefined;
  chinese_address: string;
  english_address: string;
  phone_number: string;
  house_phone_number: string;
  email: string;
  bank_account_holder: string;
  bank_name: string;
  bank_account_number: string;
  emergency_contact_name: string;
  emergency_contact_relationship: string;
  emergency_contact_phone_number: string;
  emergency_contact_house_number: string;
}

// Custom hook for token validation
const useTokenValidation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoading } = useLoading();

  const query = new URLSearchParams(location.search);
  const queryToken = query.get("token");
  
  const queryBrand = query.get("brand");
  const isFromExternal = queryBrand !== null && queryToken !== null;
  
  let brandString = "";
  try {
    brandString = jseu.encoder.decodeBase64(query.get("brand").toString()).toString();
  } catch (error) {
    navigate("/thankYou", { state: { tokenExp: true, invalidUrl: true } });
  }

  useEffect(() => {
    if (!["GOKU", "PM"].includes(brandString)) {
      navigate("/thankYou", { state: { tokenExp: true, invalidUrl: true } });
    }
    const CheckTokenExp = async () => {
      if (!queryToken) {
        if (!isFromExternal && !location.pathname.includes("/main")) {
          navigate("/main");
        }
        return;
      }

      setLoading(true);
      HttpClient().CheckTokenExpApi(
        { queryToken },
        (data) => {
          if (data?.paginationInfo?.tokenExp) {
            navigate("/thankYou", { state: { tokenExp: true } });
          }
          if (data?.paginationInfo?.isCompleted) {
            navigate("/thankYou", { state: { tokenExp: false } });
          }
          setLoading(false);
        },
        (error) => {
          if (error?.response?.data?.paginationInfo?.tokenExp) {
            navigate("/thankYou", { state: { tokenExp: true } });
          }
          if (error?.response?.data?.paginationInfo?.isCompleted) {
            navigate("/thankYou", { state: { tokenExp: false } });
          }
          setLoading(false);
        }
      );
    };

    CheckTokenExp();
  }, [queryToken, navigate, setLoading, isFromExternal, location.pathname]);

  return { isFromExternal, brandString, queryToken };
};

const StaffPersonalInfoForm: React.FC = () => {
  const classes = useStyles();
  const { isFromExternal, brandString, queryToken } = useTokenValidation();
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const location = useLocation();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
  const [submitFormError, setSubmitFormError] = useState<Partial<FormInputs>>(
    {} as Partial<FormInputs>
  );

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Convert queryBrand to string if it's Uint8Array

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      english_name: "",
      chinese_name: "",
      english_name_alias: "",
      hkid: "",
      hkid_last_digit: "",
      date_of_birth: "",
      nationality: "",
      gender: undefined,
      marriage_status: undefined,
      chinese_address: "",
      english_address: "",
      phone_number: "",
      house_phone_number: "",
      email: "",
      bank_account_holder: "",
      bank_name: "",
      bank_account_number: "",
      emergency_contact_name: "",
      emergency_contact_relationship: "",
      emergency_contact_phone_number: "",
      emergency_contact_house_number: "",
    },
    shouldFocusError: false,
  });

  const onSubmit = async (data: FormInputs) => {
    setLoading(true);
    setApiErrorMessage("");
    setSubmitFormError({});
    const param = {
      english_name: data.english_name,
      english_name_alias: data.english_name_alias,
      chinese_name: data.chinese_name,
      date_of_birth: data.date_of_birth,
      hkid: data.hkid,
      hkid_last_digit: data.hkid_last_digit,
      ...(data.nationality ? { nationality: data.nationality } : {}),
      gender: data.gender,
      marriage_status: data.marriage_status,
      chinese_address: data.chinese_address,
      english_address: data.english_address,
      phone_number: data.phone_number,
      email: data.email,
      bank_account_holder: data.bank_account_holder,
      bank_name: data.bank_name,
      bank_account_number: data.bank_account_number,
      emergency_contact_name: data.emergency_contact_name,
      emergency_contact_relationship: data.emergency_contact_relationship,
      emergency_contact_phone_number: data.emergency_contact_phone_number,
      ...(data.house_phone_number
        ? { house_phone_number: data.house_phone_number }
        : { house_phone_number: null }),
      ...(data.emergency_contact_house_number
        ? {
            emergency_contact_house_number: data.emergency_contact_house_number,
          }
        : { emergency_contact_house_number: null }),
      brand: brandString || "PM",
      ...(queryToken ? { token: queryToken } : {}),
      ...(location.state?.area ? { area: location.state.area } : {}),
      ...(isFromExternal ? { link: window.location.href } : {}),
    };

    try {
      let response;
      // Check if we're updating or creating
      if (location.state?.id) {
        response = await HttpClient().UpdateStaffFormApi({
          id: location.state.id,
          param,
          onlyUpdateOnce: true,
        });
      } else {
        response = await HttpClient().CreateStaffFormApi({
          param,
        });
      }

      if (response?.success) {
        setSnackbar({
          open: true,
          message: "提交成功",
          severity: "success",
        });

        // Call CreateThreeDoc after successful form submission for new records
        if (!location.state?.id && response.data?.id) {
          //await CreateThreeDoc(response.data.id);
        }

        if (isFromExternal) {
          navigate("/thankYou", { replace: true });
        } else {
          navigate("/main");
        }
      } else {
        setSnackbar({
          open: true,
          message: response?.message || "提交失敗",
          severity: "error",
        });
      }
    } catch (error: any) {
      setSubmitFormError(error?.response?.data?.error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || error?.message || "提交失敗",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const onInvalid = (errors: any) => {
    setSubmitFormError({});
    setSnackbar({
      open: true,
      message: "資料填寫不正確，請檢查",
      severity: "error",
    });
  };

 

  return (
    <Box className={classes.container}>
      <Container>
        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <Paper className={classes.paper}>
            <CompanyHeader brand={isFromExternal ? brandString : "PM"} />
            <Box className={classes.headerContainer}>
              <Typography variant="h6" className={classes.title}>
                員工個人資料
              </Typography>
              <Box className={classes.subtitle}>* 為必填項目</Box>
            </Box>

            <StaffFormFields
              register={register}
              getValues={getValues}
              control={control}
              errors={{ ...errors, ...transformFormErrors(submitFormError) }}
            />
          </Paper>
          <SubmitButton />
        </form>
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default StaffPersonalInfoForm;

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: "#F0EFF6",
    padding: theme.spacing(3),
    minHeight: "100vh",
    [`@media (max-width: 600px)`]: {
      padding: "24px 0",
    },
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    [`@media (max-width: 960px)`]: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      margin: "20px 0",
    },
  },
  paper: {
    padding: "20px 5%",
    maxWidth: 1280,
    margin: "0 auto",
    position: "relative",
    borderRadius: "32px",
    boxShadow: "0px 0px 15px 0px #0000001A",
    [`@media (max-width: 960px)`]: {
      padding: "20px 5% 5px 5%",
    },
    [`@media (max-width: 600px)`]: {
      borderRadius: "16px",
    },
  },
  title: {
    fontSize: "20px",
    letterSpacing: "4%",
    marginBottom: theme.spacing(3),
    color: "#454C96",
    fontWeight: 500,
    marginTop: "60px",
    [`@media (max-width: 960px)`]: {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  subtitle: {
    color: "#84909F",
    fontSize: "16px",
    fontWeight: 400,
    marginBottom: "30px",
    [`@media (max-width: 960px)`]: {
      marginBottom: 0,
    },
  },
}));